import React from 'react';

import { Box, Text } from '@adsk/alloy-react-box';
import { displayDate } from '@adsk/alloy-react-datetime';
import { AlertWarningFilledIcon } from '@adsk/alloy-react-icon';
import Tooltip, { OverflowTooltip } from '@adsk/alloy-react-tooltip';

import {
  CustomAttributesTypes,
  MULTI_SELECT_DISPLAY_LIMIT,
  CUSTOM_FIELDS_CELL_STYLES,
  EMPTY_VALUE,
} from '../../consts';
import { useTranslation } from '../../i18n';
import {
  SubmittalsItem,
  CustomAttribute,
  SingleSelectValue,
  MultiSelectValue,
} from '../../types';

interface CustomFieldCellProps {
  itemData: SubmittalsItem;
  columnId: string;
}

const iconWrapperStyles: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  flexShrink: 0,
  marginRight: 4,
  verticalAlign: 'middle',
};

const RemovedValueIcon: React.FC = () => (
  <span style={iconWrapperStyles}>
    <AlertWarningFilledIcon size={14} />
  </span>
);

const RemovedValueDisplay: React.FC<{ value: string }> = ({ value }) => {
  const { t } = useTranslation();

  return (
    <Tooltip content={`${value} (${t('REMOVED')})`}>
      <Box width="100%">
        <Box typography="ellipsis">
          <RemovedValueIcon />
          {value}
        </Box>
      </Box>
    </Tooltip>
  );
};

const normalizeMultiSelectValues = (
  values: MultiSelectValue[],
): { value: string; key: string; isRemoved: boolean }[] => {
  return values.map((item, index) => ({
    value: item.value,
    key: `${item.value}-${index}`,
    isRemoved: item.isRemoved,
  }));
};

const MultipleValuesDisplay = ({ value }: { value: MultiSelectValue[] }) => {
  const { t } = useTranslation();
  const normalizedValues = normalizeMultiSelectValues(value);

  if (normalizedValues.length === 0) {
    return <div>{EMPTY_VALUE}</div>;
  }

  const firstValue = normalizedValues[0];

  if (normalizedValues.length === 1) {
    if (!firstValue.isRemoved) {
      return <OverflowTooltip>{firstValue.value}</OverflowTooltip>;
    }

    return <RemovedValueDisplay value={firstValue.value} />;
  }

  // Multiple values case
  const tooltipContent = (
    <>
      {normalizedValues.slice(0, MULTI_SELECT_DISPLAY_LIMIT).map((entity) => (
        <div key={entity.key}>
          {entity.value}
          {entity.isRemoved && ` (${t('REMOVED')})`}
        </div>
      ))}
      {normalizedValues.length > MULTI_SELECT_DISPLAY_LIMIT && (
        <div>
          {t('CUSTOM_FIELD_MORE_OPTIONS', {
            extraCount: normalizedValues.length - MULTI_SELECT_DISPLAY_LIMIT,
          })}
        </div>
      )}
    </>
  );

  return (
    <Tooltip content={tooltipContent} trigger={Tooltip.TRIGGER_EVENTS.HOVER}>
      <OverflowTooltip>
        <Text>
          {firstValue.isRemoved && <RemovedValueIcon />}
          {firstValue.value}
        </Text>
        <br />
        <Text color="charcoal700">
          {t('CUSTOM_FIELD_MORE_OPTIONS', {
            extraCount: normalizedValues.length - 1,
          })}
        </Text>
      </OverflowTooltip>
    </Tooltip>
  );
};

const CustomFieldCell: React.FC<CustomFieldCellProps> = ({
  itemData,
  columnId,
}) => {
  const { t } = useTranslation();

  const customAttribute = (itemData.customAttributes || []).find(
    (attribute: CustomAttribute) => attribute.id === columnId,
  );

  const value = customAttribute?.value;
  const customFieldType = customAttribute?.type || '';

  if (
    value === undefined ||
    value === null ||
    (Array.isArray(value) && value.length === 0) ||
    (typeof value === 'object' && Object.keys(value).length === 0)
  ) {
    return <div>{EMPTY_VALUE}</div>;
  }

  const renderContent = () => {
    switch (customFieldType) {
      case CustomAttributesTypes.TEXT:
      case CustomAttributesTypes.LONG_TEXT: {
        const textValue = value as string;
        return (
          <Tooltip content={textValue}>
            <div style={CUSTOM_FIELDS_CELL_STYLES[CustomAttributesTypes.TEXT]}>
              {textValue}
            </div>
          </Tooltip>
        );
      }
      case CustomAttributesTypes.NUMBER:
        return (
          <OverflowTooltip>
            <div
              style={CUSTOM_FIELDS_CELL_STYLES[CustomAttributesTypes.NUMBER]}
            >
              {value as number}
            </div>
          </OverflowTooltip>
        );
      case CustomAttributesTypes.DATE: {
        const formatDate = displayDate(new Date(value as Date | string));
        return <OverflowTooltip>{formatDate}</OverflowTooltip>;
      }
      case CustomAttributesTypes.SINGLE_SELECT: {
        const selectValue = value as SingleSelectValue;

        if (!selectValue.isRemoved) {
          return <OverflowTooltip>{selectValue.value}</OverflowTooltip>;
        }

        return <RemovedValueDisplay value={selectValue.value} />;
      }
      case CustomAttributesTypes.MULTI_SELECT:
        return (
          <MultipleValuesDisplay
            value={Array.isArray(value) ? (value as MultiSelectValue[]) : []}
          />
        );
      case CustomAttributesTypes.BOOLEAN:
        return <OverflowTooltip>{value?.toString()}</OverflowTooltip>;
      default:
        return (
          <OverflowTooltip>{t('CUSTOM_FIELD_NOT_SUPPORTED')}</OverflowTooltip>
        );
    }
  };

  return <div style={{ width: '100%' }}>{renderContent()}</div>;
};

CustomFieldCell.displayName = 'SubmittalsCustomFieldCell';
export default CustomFieldCell;
